import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Navigate, Route, Routes } from "react-router-dom";

import "./styles/app.css";

import WeatherScene from "./components/WeatherScene/WeatherScene";
import { setWeatherFavicon } from "./utils/favicon";

import { UI } from "./constants/uiText";
import { DEFAULT_CITY, MOLDOVA_CITIES } from "./constants/cities";

import {
  getWeatherLabel,
  getWeatherIcon,
  mapWeatherCodeToSceneType,
  formatHour,
  formatDay,
  getWeatherAdvice,
  getExtraAdvice,
  getNearestCity,
  getTimeOfDay,
} from "./utils/weatherUtils";

function WeatherPage({ lang }) {
  const cityTabsRef = useRef(null);
  const sliderRef = useRef(null);

  const [weather, setWeather] = useState(null);
  const [dragWidth, setDragWidth] = useState(0);
  const [cityTabsDragWidth, setCityTabsDragWidth] = useState(0);
  const [activeCity, setActiveCity] = useState(DEFAULT_CITY.id);
  const [forecastMode, setForecastMode] = useState("24h");

  const fetchWeather = async (city) => {
    try {
      const res = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current=temperature_2m,weather_code,is_day,wind_speed_10m&hourly=temperature_2m,precipitation_probability,weather_code&daily=temperature_2m_max,temperature_2m_min,weather_code,sunrise,sunset&forecast_days=16&forecast_hours=24&timezone=auto`
      );

      if (!res.ok) {
        throw new Error(`HTTP error ${res.status}`);
      }

      const data = await res.json();

      if (!data?.current || !data?.hourly || !data?.daily) {
        throw new Error("Invalid weather response");
      }

      const timeOfDay = getTimeOfDay(
        data.current.time,
        data.daily.sunrise[0],
        data.daily.sunset[0]
      );

      const sceneWeather = mapWeatherCodeToSceneType(
        data.current.weather_code,
        data.current.wind_speed_10m
      );

      setWeatherFavicon(sceneWeather, timeOfDay);

      const hourlyData = data.hourly.time.map((time, index) => ({
        time: formatHour(time, index, lang),
        temp: Math.round(data.hourly.temperature_2m[index]),
        rain:
          data.hourly.precipitation_probability[index] > 0
            ? Math.round(data.hourly.precipitation_probability[index])
            : null,
        icon: getWeatherIcon(
          data.hourly.weather_code[index],
          data.current.is_day
        ),
        active: index === 0,
      }));

      const weeklyData = data.daily.time.map((time, index) => ({
        day: formatDay(time, index, lang),
        icon: getWeatherIcon(data.daily.weather_code[index], 1),
        high: Math.round(data.daily.temperature_2m_max[index]),
        low: Math.round(data.daily.temperature_2m_min[index]),
      }));

      setWeather({
        cityId: city.id,
        city: city.name[lang],
        currentTemp: Math.round(data.current.temperature_2m),
        description: getWeatherLabel(data.current.weather_code, lang),
        high: Math.round(data.daily.temperature_2m_max[0]),
        low: Math.round(data.daily.temperature_2m_min[0]),
        hourly: hourlyData,
        weekly: weeklyData,
        sceneWeather,
        timeOfDay,
        error: false,
      });
    } catch (error) {
      console.error("Weather fetch error:", error);

      setWeather({
        cityId: city.id,
        city: city.name[lang],
        currentTemp: "--",
        description: UI[lang].error,
        high: "--",
        low: "--",
        hourly: [],
        weekly: [],
        sceneWeather: "clear",
        timeOfDay: "day",
        error: true,
      });
    }
  };

  useEffect(() => {
    const initWeather = () => {
      setActiveCity(DEFAULT_CITY.id);
      fetchWeather(DEFAULT_CITY);

      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          const nearestCity = getNearestCity(
            lat,
            lon,
            MOLDOVA_CITIES,
            DEFAULT_CITY
          );

          if (nearestCity.id !== DEFAULT_CITY.id) {
            setActiveCity(nearestCity.id);
            fetchWeather(nearestCity);
          }
        },
        () => {},
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 300000,
        }
      );
    };

    initWeather();
  }, [lang]);

  useEffect(() => {
    if (sliderRef.current) {
      const viewportWidth = sliderRef.current.offsetWidth;
      const track = sliderRef.current.querySelector(".cards-track");

      if (track) {
        const trackWidth = track.scrollWidth;
        setDragWidth(Math.max(trackWidth - viewportWidth, 0));
      }
    }
  }, [weather, forecastMode]);

  useEffect(() => {
    if (cityTabsRef.current) {
      const viewportWidth = cityTabsRef.current.offsetWidth;
      const track = cityTabsRef.current.querySelector(".city-tabs-track");

      if (track) {
        const trackWidth = track.scrollWidth;
        setCityTabsDragWidth(Math.max(trackWidth - viewportWidth, 0));
      }
    }
  }, [weather]);

  const handleCitySelect = (city) => {
    setActiveCity(city.id);
    fetchWeather(city);
  };

  const baseUrl = "https://pagoda.md";
  const currentUrl = lang === "ru" ? `${baseUrl}/ru` : baseUrl;

  if (!weather) {
    return (
      <>
        <Helmet>
          <html lang={lang} />
          <title>
            {lang === "ru"
              ? "Pagoda.md — Погода в Молдове | Кишинёв, Бельцы, Кагул"
              : "Pagoda.md — Prognoza meteo în Moldova | Chișinău, Bălți, Cahul"}
          </title>
          <meta
            name="description"
            content={
              lang === "ru"
                ? "Точный прогноз погоды по Молдове. Кишинёв, Бельцы, Кагул, Оргеев, Комрат, Сороки и Унгены. Почасовой прогноз и прогноз на 16 дней."
                : "Prognoza meteo precisă pentru Moldova. Vezi vremea în Chișinău, Bălți, Cahul, Orhei, Comrat, Soroca și Ungheni. Prognoză pe ore și pe 16 zile."
            }
          />
          <meta
            property="og:title"
            content={
              lang === "ru"
                ? "Pagoda.md — Погода в Молдове"
                : "Pagoda.md — Prognoza meteo în Moldova"
            }
          />
          <meta
            property="og:description"
            content={
              lang === "ru"
                ? "Точный прогноз погоды по Молдове. Почасовой прогноз и прогноз на 16 дней."
                : "Prognoza meteo precisă pentru Moldova. Prognoză pe ore și pe 16 zile."
            }
          />
          <meta property="og:url" content={currentUrl} />
          <link rel="canonical" href={currentUrl} />
          <link rel="alternate" href={baseUrl} hreflang="ro" />
          <link rel="alternate" href={`${baseUrl}/ru`} hreflang="ru" />
          <link rel="alternate" href={baseUrl} hreflang="x-default" />
        </Helmet>

        <div className="app">
          <WeatherScene city={activeCity} weather="clear" timeOfDay="day" />
          <div className="weather">
            <div className="current">
              <h1>{UI[lang].loading}</h1>
            </div>
          </div>
        </div>
      </>
    );
  }

  const forecastItems =
    forecastMode === "24h"
      ? weather.hourly
      : forecastMode === "7d"
      ? weather.weekly.slice(0, 7)
      : forecastMode === "16d"
      ? weather.weekly
      : [];

  const pageTitle =
    lang === "ru"
      ? `${weather.currentTemp}° ${weather.city} — Погода в Молдове | Pagoda.md`
      : `${weather.currentTemp}° ${weather.city} — Vremea în Moldova | Pagoda.md`;

  const pageDescription =
    lang === "ru"
      ? `Точный прогноз погоды в ${weather.city}. Сейчас ${weather.currentTemp}°, ${weather.description.toLowerCase()}. Почасовой прогноз и прогноз на 16 дней на Pagoda.md.`
      : `Prognoza meteo precisă pentru ${weather.city}. Acum ${weather.currentTemp}°, ${weather.description.toLowerCase()}. Prognoză pe ore și pe 16 zile pe Pagoda.md.`;

  const ogTitle =
    lang === "ru"
      ? `Погода в ${weather.city} — Pagoda.md`
      : `Vremea în ${weather.city} — Pagoda.md`;

  const ogDescription =
    lang === "ru"
      ? `${weather.currentTemp}° в ${weather.city}. ${weather.description}. Почасовой прогноз и прогноз на 16 дней.`
      : `${weather.currentTemp}° în ${weather.city}. ${weather.description}. Prognoză pe ore și pe 16 zile.`;

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={currentUrl} />
        <link rel="canonical" href={currentUrl} />
        <link rel="alternate" href={baseUrl} hreflang="ro" />
        <link rel="alternate" href={`${baseUrl}/ru`} hreflang="ru" />
        <link rel="alternate" href={baseUrl} hreflang="x-default" />
      </Helmet>

      <div className="app">
        <WeatherScene
          key={`${weather.cityId}-${weather.sceneWeather}-${weather.timeOfDay}`}
          city={weather.cityId}
          weather={weather.sceneWeather}
          timeOfDay={weather.timeOfDay}
        />

        <div className="weather">
          <div className="city-tabs-viewport" ref={cityTabsRef}>
            <motion.div
              className="city-tabs-track"
              drag="x"
              dragConstraints={{
                left: -Math.max(cityTabsDragWidth, 0),
                right: 0,
              }}
              dragElastic={0.05}
              whileTap={{ cursor: "grabbing" }}
            >
              {MOLDOVA_CITIES.map((city) => (
                <button
                  key={city.id}
                  className={`city-tab ${
                    activeCity === city.id ? "active" : ""
                  }`}
                  onClick={() => handleCitySelect(city)}
                >
                  {city.name[lang]}
                </button>
              ))}
            </motion.div>
          </div>

          <div className="current">
            <h1>{weather.city}</h1>
            <div className="temp">{weather.currentTemp}°</div>
            <div className="desc">{weather.description}</div>
            <div className="hl">
              {UI[lang].highLow(weather.high, weather.low)}
            </div>

            {!weather.error && (
              <>
                <div className="advice">{getWeatherAdvice(weather, lang)}</div>
                <div className="advice secondary-advice">
                  {getExtraAdvice(weather, lang)}
                </div>
              </>
            )}
          </div>

          <div className="forecast">
            <div className="forecast-top">
              <div className="forecast-tabs-scroll">
                <button
                  className={`forecast-switch ${
                    forecastMode === "24h" ? "active" : ""
                  }`}
                  onClick={() => setForecastMode("24h")}
                >
                  24 ore
                </button>

                <button
                  className={`forecast-switch ${
                    forecastMode === "7d" ? "active" : ""
                  }`}
                  onClick={() => setForecastMode("7d")}
                >
                  7 zile
                </button>

                <button
                  className={`forecast-switch ${
                    forecastMode === "16d" ? "active" : ""
                  }`}
                  onClick={() => setForecastMode("16d")}
                >
                  16 zile
                </button>

                <button
                  className="forecast-switch forecast-switch--disabled"
                  disabled
                  type="button"
                >
                  1 lună
                </button>
              </div>
            </div>

            <div className="cards-viewport" ref={sliderRef}>
              <motion.div
                className="cards-track"
                drag="x"
                dragConstraints={{ left: -Math.max(dragWidth, 0), right: 0 }}
                dragElastic={0.05}
                whileTap={{ cursor: "grabbing" }}
                key={forecastMode}
              >
                {forecastMode === "24h" &&
                  forecastItems.map((item, index) => (
                    <div
                      key={index}
                      className={`hour-card ${item.active ? "active" : ""}`}
                    >
                      <div className="hour">{item.time}</div>
                      <div className="icon">{item.icon}</div>

                      {item.rain !== null ? (
                        <div className="rain">{item.rain}%</div>
                      ) : (
                        <div className="rain empty" />
                      )}

                      <div className="degree">{item.temp}°</div>
                    </div>
                  ))}

                {(forecastMode === "7d" || forecastMode === "16d") &&
                  forecastItems.map((item, index) => (
                    <div key={index} className="hour-card week-card">
                      <div className="hour">{item.day}</div>
                      <div className="icon">{item.icon}</div>
                      <div className="week-temps">
                        <span>{item.high}°</span>
                        <span className="week-low">{item.low}°</span>
                      </div>
                    </div>
                  ))}

                {forecastItems.length === 0 && (
                  <div className="hour-card week-card">
                    <div className="hour">—</div>
                    <div className="icon">☁️</div>
                    <div className="week-temps">
                      <span>—</span>
                      <span className="week-low">—</span>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<WeatherPage lang="ro" />} />
      <Route path="/ru" element={<WeatherPage lang="ru" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}